import { DurationMetricLabel, VendingPackageTimersDurationAuditPendingByProductListChart, toDurationParts } from '@frontend/chart/components';
import { formatTimeDifference } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadgeMapper } from '../../../modal/platform-object-badge/platform-object-badge-mapper.component';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightClientListPagination } from '../insight-client-list-pagination.component';
import { InsightId } from '../insight-registry/insight-ids';
import { VendingLongestReviewWaitsItem, useVendingLongestReviewWaitsList } from './use-vending-longest-review-waits-list';

const DETAIL_PAGE_SIZE = 25;

export interface VendingLongestReviewWaitsListProps {
    accountId: string;
    width?: InsightCardWidth;
}

/** Exact clock duration for native hover title (`HH:mm` or `Nd HH:mm`). */
const formatExactDuration = (durationSeconds: number): string => {
    const end = Date.now();
    return formatTimeDifference(new Date(end - durationSeconds * 1000).toISOString(), new Date(end).toISOString());
};

const ProductDurationRows = ({ items }: { items: VendingLongestReviewWaitsItem[] }) => {
    const intl = useIntl();
    const waitingLabel = intl.formatMessage({
        id: 'platform.analytics.insights.vending-longest-review-waits-list.waiting',
        description: 'Column header for open audit-pending wait duration on the vending longest-review-waits insights list',
        defaultMessage: 'Waiting'
    });

    return (
        <>
            <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                <div className='w-full px-1 py-2'>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</div>
                <div className='w-[160px] shrink-0 py-2 text-right'>{waitingLabel}</div>
            </div>
            {items.map((item) => (
                <div
                    key={item.productId}
                    className='border-border-default flex h-11 items-center gap-4 border-b'>
                    <div className='text-foreground-default flex w-full min-w-0 items-center px-1 text-sm font-medium'>
                        <PlatformObjectBadgeMapper
                            id={item.productId}
                            type='product_id'
                        />
                    </div>
                    <div className='flex w-[160px] shrink-0 justify-end pe-1'>
                        <span
                            className='text-background-surface bg-brand-muted inline-flex min-w-8 cursor-default items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'
                            title={formatExactDuration(item.durationSeconds)}>
                            <DurationMetricLabel {...toDurationParts(item.durationSeconds)} />
                        </span>
                    </div>
                </div>
            ))}
        </>
    );
};

export const VendingLongestReviewWaitsList = ({ accountId, width }: VendingLongestReviewWaitsListProps) => {
    const { items, isLoading, hasData, isDetailMode } = useVendingLongestReviewWaitsList(accountId);
    const [pageIndex, setPageIndex] = useState(0);

    useEffect(() => {
        setPageIndex(0);
    }, [items]);

    const pageCount = Math.max(1, Math.ceil(items.length / DETAIL_PAGE_SIZE));
    const safePageIndex = Math.min(pageIndex, pageCount - 1);
    const pageItems = isDetailMode ? items.slice(safePageIndex * DETAIL_PAGE_SIZE, safePageIndex * DETAIL_PAGE_SIZE + DETAIL_PAGE_SIZE) : items;

    return (
        <InsightCard
            insightId={InsightId.VENDING_LONGEST_REVIEW_WAITS}
            width={width}
            chartKey={VendingPackageTimersDurationAuditPendingByProductListChart.definition.key}
            title={
                <FormattedMessage
                    id='platform.analytics.insights.vending-longest-review-waits-list.title'
                    description='Title for the vending list of products with the longest open audit-pending wait on analytics insights'
                    defaultMessage='Longest waiting for review'
                />
            }
            subtitle={
                <FormattedMessage
                    id='widget.last-30-days'
                    defaultMessage='last 30 days'
                />
            }
            isLoading={isLoading}>
            {!hasData ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : isDetailMode ? (
                <div className='flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4'>
                    <div className='min-h-0 w-full min-w-0 flex-1 overflow-y-auto'>
                        <ProductDurationRows items={pageItems} />
                    </div>
                    <InsightClientListPagination
                        totalCount={items.length}
                        pageIndex={safePageIndex}
                        pageCount={pageCount}
                        onPageChange={setPageIndex}
                        navigationId='longest-review-waits'
                    />
                </div>
            ) : (
                <div className='max-h-[420px] w-full min-w-0 overflow-y-auto'>
                    <ProductDurationRows items={pageItems} />
                </div>
            )}
        </InsightCard>
    );
};
